// /src/pages/EditHolidaysPage.js
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

// These endpoints are admin-gated server-side, so every request must carry
// the JWT. credentials:"include" alone is not enough -- auth is a bearer
// header here, not a cookie.
const authHeaders = () => {
  const token = localStorage.getItem("token") || "";
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

export default function EditHolidaysPage() {
  const [rows, setRows] = useState([]);
  const [year, setYear] = useState("ALL");
  const [q, setQ] = useState("");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchAll = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch("/api/holidays", {
        headers: authHeaders(),
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const payload = await res.json();
      const items = Array.isArray(payload.holidays) ? payload.holidays : [];
      setRows(items);

      // Default year:
      // 1) current year if present in the data
      // 2) else latest year present
      // 3) else ALL
      const nowYear = new Date().getFullYear();

      const ys = Array.from(
        new Set(items.map((x) => x.year).filter((y) => y != null))
      )
        .map((y) => Number(y))
        .filter((y) => !Number.isNaN(y))
        .sort((a, b) => a - b);

      const currentSelectionValid =
        year !== "ALL" && year && ys.includes(Number(year));

      if (ys.length > 0 && (!year || year === "ALL" || !currentSelectionValid)) {
        if (ys.includes(nowYear)) {
          setYear(String(nowYear));
        } else {
          setYear(String(ys[ys.length - 1]));
        }
      } else if (ys.length === 0 && (!year || year === "ALL")) {
        setYear("ALL");
      }
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const years = useMemo(() => {
    const ys = Array.from(
      new Set(rows.map((x) => x.year).filter((y) => y != null))
    )
      .map((y) => Number(y))
      .filter((y) => !Number.isNaN(y))
      .sort((a, b) => a - b);
    return ys;
  }, [rows]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    const yearNum = year === "ALL" ? null : Number(year);

    return rows.filter((r) => {
      if (yearNum != null && Number(r.year) !== yearNum) return false;

      if (!term) return true;

      const hay = [
        r.holiday_name,
        r.year,
        r.date_actual,
        r.date_observed,
        r.is_observed ? "observed" : "not observed",
      ]
        .filter(Boolean)
        .map((x) => String(x).toLowerCase());

      return hay.some((h) => h.includes(term));
    });
  }, [rows, year, q]);

  const handleToggleObserved = async (holidayId, nextVal) => {
    // optimistic update + rollback
    const prevRows = rows;

    setRows((cur) =>
      cur.map((r) => (r.id === holidayId ? { ...r, is_observed: nextVal } : r))
    );

    try {
      const res = await fetch(`/api/holidays/${holidayId}`, {
        method: "PATCH",
        headers: authHeaders(),
        credentials: "include",
        body: JSON.stringify({ is_observed: nextVal }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Save failed (HTTP ${res.status})`);
      }

      const body = await res.json().catch(() => null);
      const saved = body?.holiday;
      if (saved && typeof saved.is_observed !== "undefined") {
        setRows((cur) =>
          cur.map((r) =>
            r.id === holidayId ? { ...r, is_observed: saved.is_observed } : r
          )
        );
      }
    } catch (e) {
      console.error(e);
      setError(String(e?.message || e));
      setRows(prevRows);
    }
  };

  return (
    <div className="container py-4">
      <div className="d-flex align-items-center gap-2 mb-3">
        <Link to="/admin" className="btn btn-outline-secondary btn-sm">
          ← Back
        </Link>
        <h1 className="h4 mb-0">Observed Holidays</h1>
        <div className="ms-auto d-flex gap-2">
          <button onClick={fetchAll} className="btn btn-outline-primary btn-sm">
            Refresh
          </button>
        </div>
      </div>

      <div className="card mb-3" style={{ borderRadius: "1rem" }}>
        <div className="card-body">
          <div className="row g-2 align-items-end">
            <div className="col-12 col-md-3">
              <label className="form-label mb-1">Year</label>
              <select
                className="form-select form-select-sm"
                value={year}
                onChange={(e) => setYear(e.target.value)}
                disabled={loading}
              >
                <option value="ALL">All years</option>
                {years.map((y) => (
                  <option key={y} value={String(y)}>
                    {y}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-12 col-md-6">
              <label className="form-label mb-1">Search</label>
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="form-control form-control-sm"
                placeholder="Search holiday name, dates…"
              />
            </div>

            <div className="col-12 col-md-3 text-md-end">
              <div className="text-muted small">
                Showing <strong>{filtered.length}</strong>{" "}
                {filtered.length === 1 ? "row" : "rows"}
              </div>
            </div>
          </div>

          <div className="text-muted small mt-2">
            Toggle <strong>Observed</strong> to control business-day logic (e.g.,
            last business day).
          </div>
        </div>
      </div>

      {loading && <div className="alert alert-secondary mb-3">Loading…</div>}
      {error && <div className="alert alert-danger mb-3">{error}</div>}

      {!loading && !error && filtered.length === 0 && (
        <div className="alert alert-warning">No holidays found.</div>
      )}

      {!loading && filtered.length > 0 && (
        <div
          className="card"
          style={{ borderRadius: "1rem", overflow: "hidden" }}
        >
          <div className="table-responsive">
            <table className="table table-sm table-striped table-bordered mb-0 align-middle">
              <thead className="table-light">
                <tr>
                  <th style={{ width: 90, fontWeight: 600 }}>Year</th>
                  <th style={{ width: 280, fontWeight: 600 }}>Holiday</th>
                  <th style={{ width: 160, fontWeight: 600 }}>Actual</th>
                  <th style={{ width: 160, fontWeight: 600 }}>Observed Date</th>
                  <th
                    style={{
                      width: 120,
                      fontWeight: 600,
                      textAlign: "center",
                    }}
                  >
                    Observed
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <code>{r.year ?? "—"}</code>
                    </td>
                    <td>
                      <code>{r.holiday_name || "—"}</code>
                    </td>
                    <td className="text-nowrap">
                      <code>{formatDate(r.date_actual)}</code>
                    </td>
                    <td className="text-nowrap">
                      <code>{formatDate(r.date_observed)}</code>
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <ObservedToggle
                        value={r.is_observed}
                        onChange={(next) => handleToggleObserved(r.id, next)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="card-footer text-muted small">
            Changes are saved immediately.
          </div>
        </div>
      )}
    </div>
  );
}

function formatDate(v) {
  if (!v) return "—";
  const s = String(v).slice(0, 10);
  const parts = s.split("-");
  if (parts.length !== 3) return s;
  const y = Number(parts[0]);
  const m = Number(parts[1]);
  const d = Number(parts[2]);
  if (!y || !m || !d) return s;
  const js = new Date(y, m - 1, d);
  return js.toLocaleDateString();
}

function ObservedToggle({ value, onChange }) {
  const checked = normalizeBool(value);
  return (
    <div className="form-check form-switch d-inline-flex justify-content-center m-0">
      <input
        className="form-check-input"
        type="checkbox"
        role="switch"
        checked={checked}
        onChange={(e) => onChange && onChange(e.target.checked)}
      />
    </div>
  );
}

function normalizeBool(v) {
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v !== 0;
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    if (["true", "1", "yes", "y", "t", "on"].includes(s)) return true;
    if (["false", "0", "no", "n", "f", "off"].includes(s)) return false;
  }
  return false;
}

